import React, { useEffect, useState } from "react";
import { Box, IconButton, InputAdornment, MenuItem, TextField } from "@mui/material";
import { InputConform, InputConformProps } from "./Input";
import {
  TypeFrequenceAssuEnum,
  TypeFrequenceEnum,
  TypeFrequenceIntEnum,
  canalEnum,
  codeAlerteEnum,
  etatAlerteEnum,
  labelLanguePDF,
  labelNatureSinistre,
  labelPartResponsabilite,
  labelSynchroFva,
  labelTypeConducteur,
  labelUsage,
  labelsCivilite,
  labelsOrigineResiliation,
  labelsStatutContrat,
  statutAlerteEnum,
  statutFileLabels,
  typeAlimEnum,
  typeInterrosLabels,
  PrecisionSinistreAltEnum,
  PrecisionSinistreEnum,
} from "model/schema";
import { FieldMetadata, useInputControl } from "@conform-to/react";
import { Clear as ClearIcon, People } from "@mui/icons-material";
import i18n from "i18next";
import { useTranslation } from "react-i18next";
import { useUser } from "utils/useGetUser";
import { Role } from "api/auth";
import {
  labelPermimetre,
  labelPermimetreFiltre,
} from "pages/GestionHabilitation/habilitationSchema";
import { PeriodiciteEnum } from "pages/ParametrageSociete/Frequences";
import { useDidUpdateEffect } from "utils/useDidUpdateEffect";

export function SelectConform({
  optionsMap,
  optionsArray,
  clearSelect = true,
  translationPrefix,
  ...props
}: InputConformProps & {
  optionsMap?: { [key: string]: string };
  optionsArray?: readonly string[];
  clearSelect?: boolean;
  translationPrefix?: string;
}) {
  const field = useInputControl(
    props.field as FieldMetadata<string, Record<string, string>, string>,
  );

  const [value, setValue] = useState<string>(() => {
    if (field.value) {
      return field.value;
    }
    if (optionsArray?.includes("Tous") || optionsMap?.["Tous"]) {
      return "Tous";
    }
    return "";
  });

  useEffect(() => {
    const nextValue = field.value ?? "";

    setValue((currentValue) => (currentValue === nextValue ? currentValue : nextValue));
  }, [field.value]);

  // Avec le select, la fonction blur n'est pas transmise automatiquement à l'input
  // Le faire manuellement pour déclencher la validation du champ de l'input
  useDidUpdateEffect(() => {
    field.blur();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const { t } = useTranslation("common");

  return (
    <InputConform
      select
      SelectProps={{ displayEmpty: true }}
      {...props}
      value={value}
      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
        setValue(event.target.value ?? "");
        if (props.onChange) {
          props.onChange(event);
        }
      }}
      InputProps={{
        sx: {
          "& .MuiSelect-select": {
            ":focus": {
              background: "inherit",
            },
          },
          "& .MuiSelect-icon": {
            display: props.InputProps?.endAdornment ? "none" : "inherit",
          },
        },
        endAdornment:
          !!!props.disabled && clearSelect && value !== "" ? (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <IconButton
                size="small"
                onClick={() => {
                  setValue("");
                  field.change("");
                  if (props.onChange) {
                    const event = {
                      target: {
                        value: "",
                        name: props.field.name,
                      },
                    } as React.ChangeEvent<HTMLInputElement>;

                    props.onChange(event);
                  }
                }}
                sx={{
                  visibility: value ? "visible" : "hidden",
                  mr: props.InputProps?.endAdornment ? 0 : 2,
                }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
              {props.InputProps?.endAdornment}
            </Box>
          ) : (
            <>{props.InputProps?.endAdornment}</>
          ),
      }}
    >
      {optionsMap &&
        Object.entries(optionsMap).map(([value, label]) => (
          <MenuItem key={value} value={value}>
            {t((translationPrefix ?? "") + label)}
          </MenuItem>
        ))}

      {optionsArray &&
        optionsArray
          .map((value) => ({
            value,
            label: i18n.t(translationPrefix ? translationPrefix + value : value, { ns: "common" }),
          }))
          .sort((a, b) => a.label.localeCompare(b.label, "fr"))
          .map((a) => (
            <MenuItem key={a.value} value={a.value}>
              {a.label}
            </MenuItem>
          ))}
    </InputConform>
  );
}

export const SelectOrigineResiliation = (props: InputConformProps) => (
  <SelectConform optionsMap={labelsOrigineResiliation} {...props} />
);

export const SelectTypeConducteur = (props: InputConformProps) => (
  <SelectConform optionsMap={labelTypeConducteur} {...props} />
);

export const SelectNatureSinistre = (props: InputConformProps) => (
  <SelectConform optionsMap={labelNatureSinistre} {...props} />
);

export const SelectLanguePDF = (props: InputConformProps) => (
  <SelectConform optionsMap={labelLanguePDF} {...props} />
);

export const SelectPrecisionSinistre = (props: InputConformProps) => (
  <SelectConform
    optionsArray={Object.values(PrecisionSinistreEnum)}
    translationPrefix="contrat.infosSinistre.precisions."
    {...props}
  />
);

export const SelectPrecisionSinistreAlt = (props: InputConformProps) => (
  <SelectConform
    optionsArray={Object.values(PrecisionSinistreAltEnum)}
    translationPrefix="contrat.infosSinistre.precisionsAlt."
    SelectProps={{
      MenuProps: {
        PaperProps: {
          style: {
            maxHeight: 48 * 10,
          },
        },
      },
    }}
    {...props}
  />
);

export const SelectPartResponsabilite = (props: InputConformProps) => (
  <SelectConform optionsMap={labelPartResponsabilite} {...props} />
);

export const SelectStatutContrat = (props: InputConformProps) => (
  <SelectConform optionsMap={labelsStatutContrat} {...props} />
);

export const SelectUsage = (props: InputConformProps) => (
  <SelectConform optionsMap={labelUsage} {...props} />
);

export const SelectCivilite = (props: InputConformProps) => (
  <SelectConform
    optionsMap={labelsCivilite}
    SelectProps={{ IconComponent: () => null }}
    InputProps={{
      endAdornment: (
        <InputAdornment position="end">
          <People color="primary" />
        </InputAdornment>
      ),
    }}
    {...props}
  />
);

export const SelectTypeFrequence = (props: InputConformProps) => {
  const user = useUser();
  const { t } = useTranslation("common");

  const optionsMap =
    user?.role === Role.INTERMEDIAIRE
      ? TypeFrequenceIntEnum
      : user?.role === Role.ASSUREUR
        ? TypeFrequenceAssuEnum
        : TypeFrequenceEnum;

  return (
    <SelectConform
      label={t("frequence.type")}
      optionsMap={optionsMap}
      translationPrefix={"frequence.code."}
      {...props}
    />
  );
};

export const SelectCodeAlerte = (props: InputConformProps) => {
  const { t } = useTranslation("common");
  return (
    <SelectConform
      label={t("contrat.alerte.codeAlerte")}
      optionsArray={codeAlerteEnum}
      translationPrefix={"contrat.alerte.code."}
      {...props}
    />
  );
};

export const SelectEtatAlerte = (props: InputConformProps) => {
  const { t } = useTranslation("common");
  return (
    <SelectConform
      label={t("contrat.alerte.etatAlerte")}
      optionsArray={etatAlerteEnum}
      translationPrefix={"contrat.alerte.etat."}
      {...props}
    />
  );
};

export const SelectStatutAlerte = (props: InputConformProps) => {
  const { t } = useTranslation("common");
  return (
    <SelectConform
      label={t("contrat.alerte.statutAlerte")}
      optionsArray={statutAlerteEnum}
      translationPrefix={"contrat.alerte.statut."}
      {...props}
    />
  );
};

export const SelectPerimetreFiltre = (props: InputConformProps) => {
  const { t } = useTranslation("common");
  return (
    <SelectConform
      label={t("habilitation.form.perimetre")}
      optionsMap={labelPermimetreFiltre}
      {...props}
    />
  );
};

export const SelectPerimetre = (props: InputConformProps) => {
  const { t } = useTranslation("common");
  return (
    <SelectConform
      label={t("habilitation.form.perimetre")}
      optionsMap={labelPermimetre}
      {...props}
    />
  );
};

export const SelectSynchroFva = (props: InputConformProps) => (
  <SelectConform
    optionsMap={labelSynchroFva}
    translationPrefix="habilitation.form.synchroFVA."
    {...props}
  />
);

export const SelectPeriodicite = (props: InputConformProps) => (
  <SelectConform
    optionsArray={PeriodiciteEnum}
    translationPrefix="frequence.periodicite.values."
    clearSelect={false}
    {...props}
  />
);

export const SelectCanal = (props: InputConformProps) => (
  <SelectConform
    optionsArray={canalEnum}
    translationPrefix="contrat.historique.action.canal."
    {...props}
  />
);

export const SelectStatutFile = (props: InputConformProps) => (
  <SelectConform optionsMap={statutFileLabels} {...props} />
);

export const SelectTypeAlim = (props: InputConformProps) => (
  <SelectConform
    optionsArray={typeAlimEnum}
    translationPrefix="contrat.historique.action.type."
    {...props}
  />
);

export const SelectTypeInterro = (props: InputConformProps) => (
  <SelectConform optionsMap={typeInterrosLabels} {...props} />
);
